import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../api/auth/AuthContext';
import { checkHealth, clearCache, HealthResponse } from '../../api/health/health-api';
import { publishCategory, publishAllCategories } from '../../api/products/products-api';
import { publishSkuSearchIndex, SkuPublishedResults } from '../../api/sku/sku-search-api';
import { resolveStorageUrl } from '../../api/storageClient';
import './Diagnostico.css';

interface ProductCategory {
  id: string;
  name: string;
}

interface ConfigEntry {
  key: string;
  label: string;
  value: string | undefined;
  sensitive?: boolean;
}

const CONFIG_ENTRIES: ConfigEntry[] = [
  { key: 'REACT_APP_ADMIN_API_BASE',          label: 'API Base URL',               value: process.env.REACT_APP_ADMIN_API_BASE },
  { key: 'REACT_APP_INTERNAL_DOMAIN',         label: 'Dominio interno',            value: process.env.REACT_APP_INTERNAL_DOMAIN },
  { key: 'REACT_APP_INTERNAL_CLIENT_ID',      label: 'Internal Client ID',         value: process.env.REACT_APP_INTERNAL_CLIENT_ID,     sensitive: true },
  { key: 'REACT_APP_INTERNAL_TENANT_ID',      label: 'Internal Tenant ID',         value: process.env.REACT_APP_INTERNAL_TENANT_ID,     sensitive: true },
  { key: 'REACT_APP_INTERNAL_API_CLIENT_ID',  label: 'Internal API Client ID',     value: process.env.REACT_APP_INTERNAL_API_CLIENT_ID, sensitive: true },
  { key: 'REACT_APP_EXTERNAL_CLIENT_ID',      label: 'External Client ID',         value: process.env.REACT_APP_EXTERNAL_CLIENT_ID,     sensitive: true },
  { key: 'REACT_APP_EXTERNAL_TENANT_NAME',    label: 'External Tenant Name',       value: process.env.REACT_APP_EXTERNAL_TENANT_NAME },
  { key: 'REACT_APP_EXTERNAL_API_CLIENT_ID',  label: 'External API Client ID',     value: process.env.REACT_APP_EXTERNAL_API_CLIENT_ID, sensitive: true },
];

function maskValue(value: string | undefined): string {
  if (!value) return '(no definido)';
  if (value.length <= 4) return '****';
  return value.substring(0, 4) + '*****';
}

type CheckState = 'idle' | 'loading' | 'ok' | 'error';

type PublishState = 'idle' | 'loading' | 'ok' | 'error';

const Diagnostico: React.FC = () => {
  const { roles } = useAuth();
  const [checkState, setCheckState] = useState<CheckState>('idle');
  const [healthData, setHealthData] = useState<HealthResponse | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [publishState, setPublishState] = useState<PublishState>('idle');
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishAllState, setPublishAllState] = useState<PublishState>('idle');
  const [publishAllError, setPublishAllError] = useState<string | null>(null);
  const [publishSkuState, setPublishSkuState] = useState<PublishState>('idle');
  const [publishSkuError, setPublishSkuError] = useState<string | null>(null);
  const [publishSkuResult, setPublishSkuResult] = useState<SkuPublishedResults | null>(null);
  const [clearCacheState, setClearCacheState] = useState<CheckState>('idle');
  const [clearCacheError, setClearCacheError] = useState<string | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [skuOpen, setSkuOpen] = useState(false);

  useEffect(() => {
    fetch(resolveStorageUrl('/data/products/product-categories.json'))
      .then(res => res.ok ? res.json() : [])
      .then((data: ProductCategory[]) => {
        setCategories(data);
        if (data.length > 0) setSelectedCategory(data[0].id);
      })
      .catch(() => {});
  }, []);

  if (!roles.includes('Admin')) {
    return <Navigate to="/admin" replace />;
  }

  const handleCheck = async () => {
    setCheckState('loading');
    setHealthData(null);
    setHealthError(null);
    await checkHealth({
      onSuccess: (data) => {
        setHealthData(data);
        setCheckState('ok');
      },
      onError: (msg) => {
        setHealthError(msg);
        setCheckState('error');
      },
      onNotFound: () => {
        setHealthError('Endpoint /health no encontrado (404)');
        setCheckState('error');
      },
    });
  };

  const handleClearCache = async () => {
    setClearCacheState('loading');
    setClearCacheError(null);
    await clearCache({
      onSuccess: () => setClearCacheState('ok'),
      onError: (msg) => { setClearCacheError(msg); setClearCacheState('error'); },
      onNotFound: () => { setClearCacheError('Endpoint no encontrado (404)'); setClearCacheState('error'); },
    });
  };

  const handlePublish = async () => {
    if (!selectedCategory) return;
    setPublishState('loading');
    setPublishError(null);
    await publishCategory(selectedCategory, {
      onSuccess: () => setPublishState('ok'),
      onError: (msg) => { setPublishError(msg); setPublishState('error'); },
      onNotFound: () => { setPublishError('Categoría no encontrada (404)'); setPublishState('error'); },
    });
  };

  const handlePublishAll = async () => {
    setPublishAllState('loading');
    setPublishAllError(null);
    await publishAllCategories({
      onSuccess: () => setPublishAllState('ok'),
      onError: (msg) => { setPublishAllError(msg); setPublishAllState('error'); },
      onNotFound: () => { setPublishAllError('Endpoint no encontrado (404)'); setPublishAllState('error'); },
    });
  };

  const handlePublishSku = async () => {
    setPublishSkuState('loading');
    setPublishSkuError(null);
    setPublishSkuResult(null);
    await publishSkuSearchIndex({
      onSuccess: (data) => { setPublishSkuResult(data); setPublishSkuState('ok'); },
      onError: (msg) => { setPublishSkuError(msg); setPublishSkuState('error'); },
      onNotFound: () => { setPublishSkuError('Endpoint no encontrado (404)'); setPublishSkuState('error'); },
    });
  };

  const toggleReveal = (key: string) => {
    setRevealed(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const isHealthy = healthData?.status?.toLowerCase() === 'healthy';

  return (
    <div className="diag-root">
      <h1 className="diag-title">Diagnóstico</h1>

      {/* Backend health */}
      <section className="diag-card">
        <div className="diag-card-header">
          <h2 className="diag-section-title">Estado del Backend</h2>
          <div className="diag-header-actions">
            <button
              className="diag-check-btn"
              onClick={handleCheck}
              disabled={checkState === 'loading'}
            >
              {checkState === 'loading' ? 'Verificando…' : 'Verificar conexión'}
            </button>
            <button
              className="diag-check-btn diag-check-btn--danger"
              onClick={handleClearCache}
              disabled={clearCacheState === 'loading'}
            >
              {clearCacheState === 'loading' ? 'Limpiando…' : 'Limpiar caché'}
            </button>
          </div>
        </div>
        {clearCacheState !== 'idle' && (
          <div className="diag-health-result">
            {clearCacheState === 'loading' && (
              <div className="diag-status diag-status--checking">
                <span className="diag-dot diag-dot--checking" />
                Limpiando caché…
              </div>
            )}
            {clearCacheState === 'ok' && (
              <div className="diag-status diag-status--ok">
                <span className="diag-dot diag-dot--ok" />
                Caché eliminado correctamente
              </div>
            )}
            {clearCacheState === 'error' && (
              <div className="diag-status diag-status--error">
                <span className="diag-dot diag-dot--error" />
                {clearCacheError}
              </div>
            )}
          </div>
        )}

        {checkState !== 'idle' && (
          <div className="diag-health-result">
            {checkState === 'loading' && (
              <div className="diag-status diag-status--checking">
                <span className="diag-dot diag-dot--checking" />
                Conectando con el backend…
              </div>
            )}
            {checkState === 'ok' && (
              <div className={`diag-status ${isHealthy ? 'diag-status--ok' : 'diag-status--warn'}`}>
                <span className={`diag-dot ${isHealthy ? 'diag-dot--ok' : 'diag-dot--warn'}`} />
                {isHealthy ? 'Backend disponible' : `Estado: ${healthData?.status ?? 'desconocido'}`}
              </div>
            )}
            {checkState === 'error' && (
              <div className="diag-status diag-status--error">
                <span className="diag-dot diag-dot--error" />
                {healthError}
              </div>
            )}

            {healthData && (
              <pre className="diag-json">
                {JSON.stringify(healthData, null, 2)}
              </pre>
            )}
          </div>
        )}
      </section>

      {/* Publish catalog */}
      <section className="diag-card">
        <div className="diag-card-header">
          <h2 className="diag-section-title">Catálogos de Productos</h2>
          <button
            className="diag-collapse-btn"
            onClick={() => setCatalogOpen(o => !o)}
            aria-expanded={catalogOpen}
            aria-label={catalogOpen ? 'Contraer sección' : 'Expandir sección'}
          >
            <svg
              width="18" height="18" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5"
              className={catalogOpen ? 'diag-chevron diag-chevron--open' : 'diag-chevron'}
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
        {catalogOpen && <>
          <p className="diag-hint">
            Genera una lista de productos en archivo estático JSON para evitar el uso de la base de datos.<br />
            Seleccione la categoría que desea actualizar desde la versión actual en la base de datos.
          </p>
          <div className="diag-publish-row">
            <select
              className="diag-publish-select"
              value={selectedCategory}
              onChange={e => { setSelectedCategory(e.target.value); setPublishState('idle'); }}
              disabled={publishState === 'loading' || categories.length === 0}
            >
              {categories.length === 0 && <option value="">Cargando categorías…</option>}
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <div className="diag-header-actions">
              <button
                className="diag-check-btn"
                onClick={handlePublish}
                disabled={publishState === 'loading' || !selectedCategory}
              >
                {publishState === 'loading' ? 'Publicando…' : 'Publicar'}
              </button>
              <button
                className="diag-check-btn"
                onClick={handlePublishAll}
                disabled={publishAllState === 'loading'}
              >
                {publishAllState === 'loading' ? 'Actualizando…' : 'Actualizar categorías'}
              </button>
            </div>
          </div>

          {publishState !== 'idle' && (
            <div className="diag-publish-result">
              {publishState === 'loading' && (
                <div className="diag-status diag-status--checking">
                  <span className="diag-dot diag-dot--checking" />
                  Publicando catálogo…
                </div>
              )}
              {publishState === 'ok' && (
                <div className="diag-status diag-status--ok">
                  <span className="diag-dot diag-dot--ok" />
                  Catálogo publicado correctamente
                </div>
              )}
              {publishState === 'error' && (
                <div className="diag-status diag-status--error">
                  <span className="diag-dot diag-dot--error" />
                  {publishError}
                </div>
              )}
            </div>
          )}

          {publishAllState !== 'idle' && (
            <div className="diag-publish-result">
              {publishAllState === 'loading' && (
                <div className="diag-status diag-status--checking">
                  <span className="diag-dot diag-dot--checking" />
                  Actualizando todas las categorías…
                </div>
              )}
              {publishAllState === 'ok' && (
                <div className="diag-status diag-status--ok">
                  <span className="diag-dot diag-dot--ok" />
                  Todas las categorías actualizadas correctamente
                </div>
              )}
              {publishAllState === 'error' && (
                <div className="diag-status diag-status--error">
                  <span className="diag-dot diag-dot--error" />
                  {publishAllError}
                </div>
              )}
            </div>
          )}
        </>}
      </section>

      {/* Publish SKU search index */}
      <section className="diag-card">
        <div className="diag-card-header">
          <h2 className="diag-section-title">Lista de Precios</h2>
          <div className="diag-header-actions">

            <button
              className="diag-collapse-btn"
              onClick={() => setSkuOpen(o => !o)}
              aria-expanded={skuOpen}
              aria-label={skuOpen ? 'Contraer sección' : 'Expandir sección'}
            >
              <svg
                width="18" height="18" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2.5"
                className={skuOpen ? 'diag-chevron diag-chevron--open' : 'diag-chevron'}
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
        </div>
        {skuOpen && <>
          <p className="diag-hint">
            Genera y descarga catálogos estáticos (raramente cambian) en el módulo de lista de precios para evitar el uso de la base de datos:
          </p>
          <ul className="diag-hint diag-hint--list">
            <li>Listado de todos los números de parte para búsquedas rápidas</li>
            <li>Descuentos y categorías</li>
            <li>Listado de documentos fuente</li>
            <li>Marcas y categorías de productos</li>
          </ul>

          <div className="diag-action-row">
            <button
              className="diag-check-btn"
              onClick={handlePublishSku}
              disabled={publishSkuState === 'loading'}
            >
              {publishSkuState === 'loading' ? 'Publicando…' : 'Publicar Catálogos'}
            </button>
          </div>

          {publishSkuState !== 'idle' && (
            <div className="diag-publish-result">
              {publishSkuState === 'loading' && (
                <div className="diag-status diag-status--checking">
                  <span className="diag-dot diag-dot--checking" />
                  Generando índice de búsqueda…
                </div>
              )}
              {publishSkuState === 'ok' && publishSkuResult && (
                <div className="diag-status diag-status--ok">
                  <span className="diag-dot diag-dot--ok" />
                  Publicado — {publishSkuResult.blobs.length} archivo(s),{' '}
                  {publishSkuResult.blobs.reduce((sum, b) => sum + Number(b.records), 0)} registros
                </div>
              )}
              {publishSkuState === 'error' && (
                <div className="diag-status diag-status--error">
                  <span className="diag-dot diag-dot--error" />
                  {publishSkuError}
                </div>
              )}
              
            </div>
          )}
        </>}
      </section>

      {/* Website configuration */}
      <section className="diag-card">
        <div className="diag-card-header">
          <h2 className="diag-section-title">Configuración del Sitio</h2>
          <button
            className="diag-collapse-btn"
            onClick={() => setConfigOpen(o => !o)}
            aria-expanded={configOpen}
            aria-label={configOpen ? 'Contraer sección' : 'Expandir sección'}
          >
            <svg
              width="18" height="18" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5"
              className={configOpen ? 'diag-chevron diag-chevron--open' : 'diag-chevron'}
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
        {configOpen && <>
        <p className="diag-hint">
          Los valores sensibles están enmascarados. Haga clic en el ojo para revelar temporalmente.
        </p>
        <table className="diag-config-table">
          <thead>
            <tr>
              <th>Variable</th>
              <th>Valor</th>
            </tr>
          </thead>
          <tbody>
            {CONFIG_ENTRIES.map(({ key, label, value, sensitive }) => {
              const isRevealed = revealed.has(key);
              const display = sensitive && !isRevealed
                ? maskValue(value)
                : (value ?? '(no definido)');

              return (
                <tr key={key}>
                  <td>
                    <span className="diag-config-label">{label}</span>
                    <code className="diag-config-key">{key}</code>
                  </td>
                  <td className="diag-config-value-cell">
                    <code className={sensitive && !isRevealed ? 'diag-masked' : ''}>
                      {display}
                    </code>
                    {sensitive && value && (
                      <button
                        className="diag-reveal-btn"
                        onClick={() => toggleReveal(key)}
                        title={isRevealed ? 'Ocultar' : 'Revelar'}
                        aria-label={isRevealed ? 'Ocultar valor' : 'Revelar valor'}
                      >
                        {isRevealed ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </>}
      </section>
    </div>
  );
};

export default Diagnostico;
